import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Row, Col, Card, Typography, Button, Image, Tag, Descriptions, Divider, Space, Statistic } from 'antd';
import { PrinterOutlined, ArrowLeftOutlined, CheckCircleOutlined, ClockCircleOutlined, WarningOutlined, EnvironmentOutlined, TagOutlined, HomeOutlined, HeartOutlined, StarOutlined, SettingOutlined, EyeOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { Footer } = Layout;

// 导航栏分类数据（从App.tsx复制）
const categories = [
  { id: 'all', name: '全部', icon: <StarOutlined />, isSpecial: true },
  { id: 'decorative', name: '装饰', icon: null },
  { id: 'functional', name: '功能', icon: null },
  { id: 'toy', name: '玩具', icon: null },
  { id: 'educational', name: '教育', icon: null },
  { id: 'custom', name: '定制', icon: <StarOutlined />, isSpecial: true },
];

// API调用 - 根据ID获取打印机详情
const fetchPrinterById = async (id: string) => {
  try {
    const response = await fetch(`/api/printers/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch printer details:', error);
    return null;
  }
};

const PrinterDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [printer, setPrinter] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPrinter = async () => {
      setLoading(true);
      try {
        const printerData = await fetchPrinterById(id || '');
        setPrinter(printerData);
      } catch (error) {
        console.error('Failed to load printer:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadPrinter();
    }
  }, [id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'green';
      case 'printing': return 'blue';
      case 'maintenance': return 'orange';
      case 'idle': return 'gray';
      default: return 'red';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <CheckCircleOutlined />;
      case 'printing': return <ClockCircleOutlined />;
      case 'maintenance': return <WarningOutlined />;
      default: return <WarningOutlined />;
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <Card loading={true} />
      </div>
    );
  }

  if (!printer) {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <Card>
          <Title level={4}>打印机未找到</Title>
          <Button type="primary" onClick={() => navigate('/printers')}>
            返回打印机列表
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
      {/* 统一Header */}
      <Row style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '16px',
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            borderLeft: '4px solid #1890ff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: '#1890ff',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <PrinterOutlined style={{ fontSize: '24px', color: 'white' }} />
              </div>
              <div>
                <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                  {printer.name}
                </Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <Tag 
                    color={getStatusColor(printer.status)}
                    icon={getStatusIcon(printer.status)}
                    style={{ fontSize: '12px', padding: '2px 8px' }}
                  >
                    {printer.status === 'available' ? '可用' : 
                     printer.status === 'printing' ? '打印中' : 
                     printer.status === 'maintenance' ? '维护中' : '空闲'}
                  </Tag>
                  <Text type="secondary">{printer.model} • {printer.location}</Text>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                type="default"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/printers')}
                style={{
                  borderRadius: '20px',
                  padding: '6px 16px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '500'
                }}
              >
                返回列表
              </Button>
              <Button
                type="primary"
                icon={<HomeOutlined />}
                onClick={() => navigate('/')}
                style={{
                  background: '#d32f2f',
                  borderColor: '#d32f2f',
                  borderRadius: '20px',
                  padding: '6px 16px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '500',
                  boxShadow: '0 2px 4px rgba(211, 47, 47, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#b71c1c';
                  e.currentTarget.style.borderColor = '#b71c1c';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(211, 47, 47, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#d32f2f';
                  e.currentTarget.style.borderColor = '#d32f2f';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(211, 47, 47, 0.2)';
                }}
              >
                返回主页
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* 视频预览 - 在上方 */}
        {printer.video_url && (
          <Col xs={24}>
            <Card title="视频介绍" style={{ marginBottom: '24px' }}>
              <div style={{
                width: '100%',
                height: '400px',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <video
                  src={printer.video_url}
                  controls
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                >
                  您的浏览器不支持视频标签。
                </video>
              </div>
            </Card>
          </Col>
        )}

        {/* 详细信息 - 在下方 */}
        <Col xs={24}>
          <Card 
            title="打印机详细信息"
            style={{ marginBottom: '24px' }}
          >
            <Row gutter={[24, 24]}>
              {/* 打印机图片 */}
              <Col xs={24} md={8}>
                <div style={{
                  width: '100%',
                  height: '300px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {printer.thumbnail_url ? (
                    <Image
                      src={printer.thumbnail_url}
                      alt={printer.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <PrinterOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />
                  )}
                </div>
              </Col>
              
              {/* 详细信息 */}
              <Col xs={24} md={16}>
                <Row gutter={[24, 24]}>
                  {/* 基本信息 */}
                  <Col xs={24} md={12}>
                    <div>
                      <Title level={5}>基本信息</Title>
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="打印机型号">
                          <Tag color="blue">{printer.model}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="位置">
                          <Space>
                            <EnvironmentOutlined />
                            <Text>{printer.location}</Text>
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="状态">
                          <Tag color={getStatusColor(printer.status)}>
                            {printer.status === 'available' ? '可用' : 
                             printer.status === 'printing' ? '打印中' : 
                             printer.status === 'maintenance' ? '维护中' : '空闲'}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="温度">
                          <Text>{printer.temperature}°C</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="创建时间">
                          <Text>{new Date(printer.created_at).toLocaleDateString()}</Text>
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  </Col>

                  {/* 打印能力 */}
                  <Col xs={24} md={12}>
                    <div>
                      <Title level={5}>打印能力</Title>
                      <div style={{ marginBottom: '12px' }}>
                        <Text strong style={{ display: 'block', marginBottom: '8px' }}>当前耗材:</Text>
                        <Space size={[4, 4]} wrap>
                          {printer.current_material ? (
                            <Tag color="green">
                              {printer.current_material.name}
                              {printer.current_material_remaining && ` (剩余: ${printer.current_material_remaining}g)`}
                            </Tag>
                          ) : (
                            <Text type="secondary">未安装耗材</Text>
                          )}
                        </Space>
                      </div>
                      <div>
                        <Text strong style={{ display: 'block', marginBottom: '8px' }}>支持材料:</Text>
                        <Space size={[4, 4]} wrap>
                          {printer.supported_materials && printer.supported_materials.length > 0 ? (
                            printer.supported_materials.map((material: any, idx: number) => (
                              <Tag key={idx} color="cyan">
                                {material.name}
                                {material.color && ` (${material.color})`}
                              </Tag>
                            ))
                          ) : (
                            <Text type="secondary">暂无支持材料信息</Text>
                          )}
                        </Space>
                      </div>
                    </div>
                  </Col>

                  {/* 状态详情 */}
                  <Col xs={24}>
                    <div>
                      <Title level={5}>状态详情</Title>
                      <Row gutter={[16, 8]}>
                        <Col xs={24} sm={12} md={6}>
                          <div>
                            <Text strong>状态: </Text>
                            <Tag color={getStatusColor(printer.status)}>
                              {printer.status === 'available' ? '可用' : 
                               printer.status === 'printing' ? '打印中' : 
                               printer.status === 'maintenance' ? '维护中' : '空闲'}
                            </Tag>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <div>
                            <Text strong>当前温度: </Text>
                            <Text>{printer.temperature}°C</Text>
                          </div>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <div>
                            <Text strong>创建时间: </Text>
                            <Text>{new Date(printer.created_at).toLocaleString()}</Text>
                          </div>
                        </Col>
                        {printer.current_job_id && (
                          <Col xs={24} sm={12} md={6}>
                            <div>
                              <Text strong>当前任务ID: </Text>
                              <Text>{printer.current_job_id}</Text>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col span={24}>
          <Card>
            <Title level={5}>使用说明</Title>
            <Paragraph>
              1. 打印文件格式支持：STL, OBJ, GCODE<br />
              2. 最大文件大小：100MB<br />
              3. 打印完成后请及时取件<br />
              4. 如有问题请联系管理员
            </Paragraph>
          </Card>
        </Col>
      </Row>

      {/* Footer */}
      <Footer style={{ 
        background: '#1f1f1f',
        padding: '40px 0 20px 0',
        color: '#ffffff',
        borderTop: '1px solid #333',
        marginTop: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', marginBottom: '30px' }}>
            {/* 左侧：平台信息 */}
            <div style={{ flex: '1', minWidth: '250px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  background: '#d32f2f',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px'
                }}>
                  <PrinterOutlined style={{ fontSize: '20px', color: 'white' }} />
                </div>
                <Title level={4} style={{ color: '#ffffff', margin: 0, fontWeight: 'bold' }}>
                  3D打印共享平台
                </Title>
              </div>
              <Text style={{ color: '#b0b0b0', fontSize: '14px', lineHeight: '1.6' }}>
                个人云展示墙，展示自己打印的成果，分享创意与快乐。
              </Text>
              <div style={{ marginTop: '16px' }}>
                <Space size="middle">
                  <Button 
                    type="text" 
                    icon={<HeartOutlined style={{ color: '#d32f2f' }} />}
                    style={{ color: '#b0b0b0', padding: '0' }}
                  >
                    关注我
                  </Button>
                  <Button 
                    type="text" 
                    icon={<StarOutlined style={{ color: '#ffb300' }} />}
                    style={{ color: '#b0b0b0', padding: '0' }}
                  >
                    收藏网站
                  </Button>
                </Space>
              </div>
            </div>

            {/* 中间：快速链接 */}
            <div style={{ flex: '1', minWidth: '200px', marginBottom: '20px' }}>
              <Title level={5} style={{ color: '#ffffff', marginBottom: '16px', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
                快速链接
              </Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <a href="/" style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', transition: 'color 0.3s' }}
                   onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                   onMouseLeave={(e) => e.currentTarget.style.color = '#b0b0b0'}>
                  首页
                </a>
                <a href="/printers" style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', transition: 'color 0.3s' }}
                   onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                   onMouseLeave={(e) => e.currentTarget.style.color = '#b0b0b0'}>
                  打印机管理
                </a>
                <a href="#" style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', transition: 'color 0.3s' }}
                   onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                   onMouseLeave={(e) => e.currentTarget.style.color = '#b0b0b0'}>
                  作品展示
                </a>
                <a href="#" style={{ color: '#b0b0b0', textDecoration: 'none', fontSize: '14px', transition: 'color 0.3s' }}
                   onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                   onMouseLeave={(e) => e.currentTarget.style.color = '#b0b0b0'}>
                  关于我们
                </a>
              </div>
            </div>

            {/* 右侧：联系信息 */}
            <div style={{ flex: '1', minWidth: '250px', marginBottom: '20px' }}>
              <Title level={5} style={{ color: '#ffffff', marginBottom: '16px', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
                联系我们
              </Title>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <SettingOutlined style={{ color: '#d32f2f' }} />
                  <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>技术支持: support@3dprint-share.com</Text>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CheckCircleOutlined style={{ color: '#d32f2f' }} />
                  <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>当前页面: 打印机详情</Text>
                </div>
              </div>
            </div>
          </div>

          {/* 分隔线 */}
          <Divider style={{ borderColor: '#333', margin: '20px 0' }} />

          {/* 底部：版权和备案信息 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div>
                <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>
                  © {new Date().getFullYear()} 3D打印共享平台 版权所有
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>
                  豫ICP备2026001797号-1
                </Text>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <EyeOutlined style={{ color: '#1890ff' }} />
                <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>
                  公安备案号: 豫公网安备 41010502001234号
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Footer>
    </div>
  );
};

export default PrinterDetailPage;
